/**
 * Shared type, type guard and user-facing wording for Firebase Auth errors.
 *
 * Use when catching errors from Firebase Auth APIs (signIn, signOut, password
 * reset, etc.) to safely access code and message.
 */

export interface FirebaseAuthError extends Error {
  code: string;
  message: string;
}

/**
 * Type guard for Firebase Auth errors.
 * Use in catch blocks to narrow unknown errors before reading code/message.
 */
export function isFirebaseAuthError(
  error: unknown,
): error is FirebaseAuthError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    typeof (error as FirebaseAuthError).code === 'string'
  );
}

/**
 * The SDK's own message is "Firebase: Error (auth/invalid-credential)." —
 * accurate and useless on a sign-in form. One sentence per code the auth
 * screens can actually produce; anything else gets the caller's fallback.
 *
 * The credential codes share a sentence on purpose: telling an attacker which
 * of email or password was wrong is an account-enumeration hint.
 */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-credential': 'Email or password is incorrect.',
  'auth/wrong-password': 'Email or password is incorrect.',
  'auth/user-not-found': 'Email or password is incorrect.',
  'auth/invalid-email': 'Email or password is incorrect.',
  'auth/too-many-requests':
    'Too many attempts. Reset your password or try again later.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/network-request-failed':
    'Could not reach the sign-in service. Check your connection.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/expired-action-code':
    'This link has expired. Please request a new password reset from the sign-in page.',
  'auth/invalid-action-code':
    'This link is invalid or has already been used. Please request a new one.',
  'auth/weak-password': 'Password is too weak. Please use at least 6 characters.',
};

/**
 * A sentence fit for the screen. A known auth code maps to its wording; an
 * unknown code, a plain Error or anything else yields `fallback` — the SDK's
 * raw message is never shown.
 */
export function describeAuthError(error: unknown, fallback: string): string {
  if (isFirebaseAuthError(error)) {
    return AUTH_ERROR_MESSAGES[error.code] ?? fallback;
  }
  return fallback;
}
